import logger from '../log';
import {
  ObjectRequest,
  ObjectResponse,
  ObjectResponseMapper,
  ObjectInterceptor
} from './interfaces';
import { DetectionType } from '../reporter/constants';
import composeResponseMappers from './composeResponseMappers';
import { FetchFailed, LocalAgentFetchFailed, WontIntercept } from './errors';
import InterceptorType from './InterceptorType';
import InterceptorName from './InterceptorName';
import EventEmitter from '../common/EventEmitter';
import { InternalEvents, RequestIngestData } from '../events';
import ContentConfig from '../config/ContentConfig';
import getUrlPrefix from '../ecdn11/getUrlPrefix';
import { checkVersionGE, trimVersion } from '../util/checkVersion';
import fetchForObject from './fetchForObject';
import { getElevenData } from '../ecdn11/store11AgentInfo';
import { ElevenAgentData, ElevenDataRequestState } from '../ecdn11/interfaces';
import get11AgentInfo from '../ecdn11/get11AgentInfo';
import { dispatchDetectionEvent } from '../events/dispatch';
import isAllowedLocalAgentRequest from './utils/isAllowedLocalAgentRequest';

const log = logger.getLogger(__filename);
const { REQUEST_INGEST } = InternalEvents;
const { SUCCESS } = ElevenDataRequestState;
const { ECDN11 } = DetectionType;

export default class LocalAgentInterceptor implements ObjectInterceptor {
  responseMapper: ObjectResponseMapper | undefined;
  readonly interceptorType = InterceptorType.HTTP;
  readonly interceptorName = InterceptorName.LOCAL_AGENT;
  eventEmitter: EventEmitter;
  modifiedBaseUrl: string;
  playGuid: string;
  agentData?: ElevenAgentData;
  agentVersion?: string;
  readonly config: ContentConfig;

  constructor(opts: {
    contentId: string;
    tenantId: string;
    playGuid: string;
    token: string;
    config: ContentConfig;
  }) {
    this.eventEmitter = EventEmitter.getInstance();
    this.modifiedBaseUrl = `${getUrlPrefix(opts.config)}:${
      opts.config.agent.port
    }/tenant/${opts.tenantId}/content/${opts.contentId}`;
    this.playGuid = opts.playGuid;
    this.config = opts.config;
  }

  checkAgentGE(min: string): boolean {
    const version = this.agentVersion;
    return version ? checkVersionGE({ version, min }) : false;
  }

  modifyRequest(request: ObjectRequest): ObjectRequest {
    const modifiedUrl = new URL(this.modifiedBaseUrl);
    const origUrl = new URL(request.url);
    const origParams = origUrl.searchParams;
    const url = `${origUrl.origin}${origUrl.pathname}`;
    // set original params
    for (const [key, value] of origParams.entries()) {
      modifiedUrl.searchParams.append(key, value);
    }

    modifiedUrl.searchParams.set('url', url);
    if (this.checkAgentGE('11.4')) {
      request.headers.set('X-Kollective-ObjectId', request.objectId);
    }
    if (this.checkAgentGE('11.2')) {
      request.headers.set('X-Kollective-Playguid', this.playGuid);
    } else {
      log.info(
        'Not using header for playGuid as agent version is too low.',
        `Minimum 11.2; Current Version ${this.agentVersion};`
      );
      modifiedUrl.searchParams.set('playguid', this.playGuid);
    }
    return { ...request, modifiedUrl: modifiedUrl.toString() };
  }

  registerResponseMapper(mapper: ObjectResponseMapper): void {
    if (this.responseMapper) {
      this.responseMapper = composeResponseMappers(this.responseMapper, mapper);
    } else {
      this.responseMapper = mapper;
    }
  }

  shouldIntercept(request: ObjectRequest): boolean {
    return isAllowedLocalAgentRequest(this.config.app as string, request);
  }

  checkForVersionError(): string | undefined {
    if (this.agentData) {
      return undefined;
    }
    const agentDataContainer = getElevenData(this.playGuid);
    if (agentDataContainer.state === ElevenDataRequestState.NOTCALLED) {
      // We should have already called to get the agent information as part of
      // the initialization should be part of getting the priming data, but there
      // is a small chance that the pre-priming content config doesn't have the
      // information for the agent. Adding a detection event so that we can track
      // this issue on the backend.
      dispatchDetectionEvent({
        data: { message: 'agent detection triggered from interceptor' },
        t: Date.now(),
        type: ECDN11
      });
      const controller = new AbortController();
      get11AgentInfo(this.config, controller, this.playGuid);
    }
    if (agentDataContainer.state === SUCCESS) {
      this.agentData = agentDataContainer.data;
      this.agentVersion = trimVersion(this.agentData.version);
      const message = 'Agent data does not contain version';
      return this.agentVersion ? undefined : message;
    }
    const { state } = agentDataContainer;
    return `no local agent version found, agent data config request state: ${state}`;
  }

  async handle(request: ObjectRequest): Promise<ObjectResponse> {
    const errorMessage = this.checkForVersionError();
    // Only handle a request if we have a version. All supported agents have a
    // version, if we don't have the agent version we don't have enough
    // information to make a modification request.
    if (errorMessage) {
      const data = { message: errorMessage };
      dispatchDetectionEvent({ type: ECDN11, data, t: Date.now() });
      return Promise.reject(new Error(errorMessage));
    }
    const modified = this.modifyRequest(request);
    try {
      if (!this.shouldIntercept(request)) {
        log.debug('Marked non-peerable, will not intercept', request);
        return Promise.reject(new WontIntercept());
      }
      const response = await fetchForObject(modified);
      if (response.status >= 400) {
        log.debug('Request failed', response.status, response);
        // Drop back to other interceptors.
        return Promise.reject(new LocalAgentFetchFailed(request, response));
      }
      const data = response.body;
      // Send the response as a message to allow for caching and transfer records
      // Need to clone array buffer for caching.
      if (data && (data as ArrayBuffer).byteLength) {
        const { url, modifiedUrl } = modified;
        log.debug('From 11 Agent', (data as ArrayBuffer).byteLength, {
          response,
          url,
          modifiedUrl
        });
      }
      // we only want to cache this data
      // we also only want to original request in the event.
      const eventData: RequestIngestData =
        data && (data as ArrayBuffer).byteLength
          ? {
              type: REQUEST_INGEST,
              data: {
                ...response,
                body: (data as ArrayBuffer).slice(0)
              },
              cacheOnly: true
            }
          : { type: REQUEST_INGEST, data: response, cacheOnly: true };
      this.eventEmitter.dispatch(eventData);
      return this.responseMapper ? this.responseMapper(response) : response;
    } catch (e) {
      // notify failure
      const error = { request, modifyRequest: modified, error: e as Error };
      this.eventEmitter.dispatch({ type: REQUEST_INGEST, error });
      log.debug('Interceptor failure:', REQUEST_INGEST, error);
      return Promise.reject(new FetchFailed(modified, e as Error));
    }
  }
}
